import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import {
  Typography,
  Link,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Divider
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import MermaidDiagram from "./MermaidDiagram";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& > *:first-child": {
        marginTop: 0
      }
    },
    h2Heading: {
      scrollMarginTop: 80,
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(2),
      fontWeight: 700,
      padding: theme.spacing(1.5, 2),
      borderRadius: 6,
      backgroundColor: theme.palette.type === "dark" ? "#1a2332" : "#f0f4ff",
      borderLeft: `4px solid ${theme.palette.primary.main}`
    },
    h3Heading: {
      scrollMarginTop: 80,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1),
      fontWeight: 600,
      color: theme.palette.primary.dark
    },
    heading: {
      scrollMarginTop: 80,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1)
    },
    paragraph: {
      marginBottom: theme.spacing(2),
      lineHeight: 1.7
    },
    codeBlock: {
      padding: theme.spacing(2),
      borderRadius: 6,
      backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#1e293b",
      overflowX: "auto",
      marginBottom: theme.spacing(2),
      "& code": {
        fontFamily: "'Fira Code', 'Consolas', monospace",
        fontSize: 13,
        color: "#e2e8f0",
        whiteSpace: "pre"
      }
    },
    inlineCode: {
      fontFamily: "'Fira Code', 'Consolas', monospace",
      fontSize: "0.85em",
      backgroundColor: theme.palette.type === "dark" ? "#2d2d2d" : "#e8edf5",
      padding: "2px 6px",
      borderRadius: 3,
      color: theme.palette.primary.dark
    },
    blockquote: {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(1.5, 2),
      backgroundColor: theme.palette.type === "dark" ? "#1a1a2e" : "#eef2ff",
      borderRadius: "0 8px 8px 0",
      "& p": {
        margin: 0
      }
    },
    table: {
      marginBottom: theme.spacing(3),
      borderRadius: 8,
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      "& th": {
        fontWeight: 700,
        backgroundColor: theme.palette.type === "dark" ? "#1a2332" : "#f0f4ff",
        borderBottom: `2px solid ${theme.palette.primary.light}`
      },
      "& td": {
        borderBottom: `1px solid ${theme.palette.divider}`
      },
      "& tr:last-child td": {
        borderBottom: "none"
      }
    },
    listItem: {
      marginBottom: theme.spacing(0.5),
      lineHeight: 1.7
    },
    orderedList: {
      "& > li": {
        fontWeight: 500,
        marginBottom: theme.spacing(1)
      }
    },
    divider: {
      margin: theme.spacing(4, 0)
    },
    image: {
      maxWidth: "100%",
      borderRadius: 8,
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
    }
  })
);

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={{
          h1: ({ children, id }) => (
            <Typography id={id} variant="h4" className={classes.heading} style={{ fontWeight: 700 }} gutterBottom>
              {children}
            </Typography>
          ),
          h2: ({ children, id }) => (
            <Typography id={id} variant="h5" className={classes.h2Heading} gutterBottom>
              {children}
            </Typography>
          ),
          h3: ({ children, id }) => (
            <Typography id={id} variant="h6" className={classes.h3Heading} gutterBottom>
              {children}
            </Typography>
          ),
          h4: ({ children, id }) => (
            <Typography
              id={id}
              variant="subtitle1"
              className={classes.heading}
              style={{ fontWeight: 600 }}
              gutterBottom
            >
              {children}
            </Typography>
          ),
          p: ({ children }) => (
            <Typography variant="body1" className={classes.paragraph}>
              {children}
            </Typography>
          ),
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer" color="primary">
              {children}
            </Link>
          ),
          table: ({ children }) => (
            <Table size="small" className={classes.table}>
              {children}
            </Table>
          ),
          thead: ({ children }) => <TableHead>{children}</TableHead>,
          tbody: ({ children }) => <TableBody>{children}</TableBody>,
          tr: ({ children }) => <TableRow>{children}</TableRow>,
          th: ({ children }) => <TableCell><strong>{children}</strong></TableCell>,
          td: ({ children }) => <TableCell>{children}</TableCell>,
          code: ({ inline, className, children }: any) => {
            const match = /language-(\w+)/.exec(className || "");
            const lang = match ? match[1] : "";

            if (!inline && lang === "mermaid") {
              return <MermaidDiagram chart={String(children).trim()} />;
            }

            if (inline) {
              return <code className={classes.inlineCode}>{children}</code>;
            }

            return (
              <Paper elevation={2} className={classes.codeBlock}>
                <code>{children}</code>
              </Paper>
            );
          },
          pre: ({ children }) => <>{children}</>,
          blockquote: ({ children }) => (
            <Paper elevation={0} className={classes.blockquote}>
              {children}
            </Paper>
          ),
          hr: () => <Divider className={classes.divider} />,
          ol: ({ children }) => <ol className={classes.orderedList}>{children}</ol>,
          li: ({ children }) => <li className={classes.listItem}>{children}</li>,
          img: ({ src, alt }) => (
            <img src={src} alt={alt} className={classes.image} />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
